import {
    collection,
    addDoc,
    getDocs,
    doc,
    getDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    Timestamp,
    serverTimestamp,
} from "firebase/firestore";
import { firestore } from "./firebase";
import { Artwork, OperationHistory } from "../types/artwork";
import { Contact } from "../types/contact";

export const addArtwork = async (artwork: Omit<Artwork, "id">) => {
    try {
        const docRef = await addDoc(collection(firestore, "artworks"), {
            ...artwork,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
        });
        return docRef.id;
    } catch (error) {
        console.error("Error adding artwork: ", error);
        throw error;
    }
};

export const getArtworks = async (): Promise<Artwork[]> => {
    try {
        const querySnapshot = await getDocs(
            query(collection(firestore, "artworks"), orderBy("createdAt", "desc"))
        );
        return querySnapshot.docs.map((doc) => {
            const data = doc.data();
            return {
                id: doc.id,
                ...data,
                createdAt: data.createdAt?.toDate() || new Date(),
                updatedAt: data.updatedAt?.toDate() || new Date(),
            } as Artwork;
        });
    } catch (error) {
        console.error("Error getting artworks: ", error);
        throw error;
    }
};

export const getArtwork = async (id: string): Promise<Artwork | null> => {
    try {
        const docRef = doc(firestore, "artworks", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            const data = docSnap.data();
            return {
                id: docSnap.id,
                ...data,
                createdAt: data.createdAt?.toDate() || new Date(),
                updatedAt: data.updatedAt?.toDate() || new Date(),
            } as Artwork;
        }
        return null;
    } catch (error) {
        console.error("Error getting artwork: ", error);
        throw error;
    }
};

export const updateArtwork = async (id: string, artwork: Partial<Artwork>) => {
    try {
        const docRef = doc(firestore, "artworks", id);
        await updateDoc(docRef, {
            ...artwork,
            updatedAt: Timestamp.now(),
        });
    } catch (error) {
        console.error("Error updating artwork: ", error);
        throw error;
    }
};

export const deleteArtwork = async (id: string) => {
    try {
        await deleteDoc(doc(firestore, "artworks", id));
    } catch (error) {
        console.error("Error deleting artwork: ", error);
        throw error;
    }
};


// Mantieni le funzioni per contact history per compatibilità
export const logContactHistory = async (artworkId: string, contact: Contact) => {
    try {
        const contactRef = collection(firestore, 'artworks', artworkId, 'contactHistory');
        const { id, ...contactData } = contact;
        await addDoc(contactRef, contactData);
    } catch (error) {
        console.error("Error logging contact history: ", error);
        throw new Error("Could not log contact history");
    }
};

export const getContactHistory = async (artworkId: string) => {
    try {
        const q = query(collection(firestore, 'artworks', artworkId, 'contactHistory'), orderBy('date', 'desc'));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            date: doc.data().date?.toDate() || new Date()
        })) as Contact[];
    } catch (error) {
        console.error("Error fetching contact history: ", error);
        throw new Error("Could not fetch contact history");
    }
};

// Aggiorna getOperationHistory per includere i nuovi campi
export const getOperationHistory = async (artworkId: string): Promise<OperationHistory[]> => {
    try {
        console.log("🔍 Getting operation history from subcollection for artwork:", artworkId);

        const operationsRef = collection(firestore, "artworks", artworkId, "operations");
        const querySnapshot = await getDocs(operationsRef);

        console.log("📊 Found", querySnapshot.docs.length, "operations in subcollection");

        const operations = querySnapshot.docs.map((doc) => {
            const data = doc.data();
            return {
                id: doc.id,
                type: data.type || "unknown",
                description: data.description || "",
                outcome: data.outcome || "",
                nextAction: data.nextAction || "",
                nextActionStatus: data.nextActionStatus || (data.nextAction ? 'pending' : undefined),
                nextActionCompletedAt: data.nextActionCompletedAt?.toDate(),
                nextActionCompletionNotes: data.nextActionCompletionNotes,
                date: data.date?.toDate ? data.date.toDate() : new Date(data.date || Date.now()),
                createdAt: data.createdAt?.toDate() || new Date(),
                updatedAt: data.updatedAt?.toDate(),
            } as OperationHistory;
        });

        operations.sort((a, b) => {
            return b.date.getTime() - a.date.getTime();
        });

        return operations;
    } catch (error) {
        console.error("Error getting operation history from subcollection:", error);
        throw error;
    }
};

// FUNZIONE duplicateArtwork CORRETTA per la struttura reale
export const duplicateArtwork = async (artworkId: string, newTitle: string): Promise<string> => {
    try {
        console.log("🔄 Starting artwork duplication for ID:", artworkId);

        // 1. Ottieni l'artwork originale
        const originalRef = doc(firestore, "artworks", artworkId);
        const originalSnap = await getDoc(originalRef);

        if (!originalSnap.exists()) {
            throw new Error("Artwork not found");
        }

        const originalData = originalSnap.data();
        console.log("✅ Original artwork found:", originalData.title);

        // 2. Crea il nuovo artwork
        const newArtwork: any = {
            ...originalData,
            title: newTitle,
            status: 'pending',
            notes: originalData.notes
                ? `Duplicated from: "${originalData.title}"\n\n${originalData.notes}`
                : `Duplicated from: "${originalData.title}"`,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
        };

        if (newArtwork.id) {
            delete newArtwork.id;
        }

        // 3. Aggiungi il nuovo artwork
        console.log("➕ Creating new artwork...");
        const newDocRef = await addDoc(collection(firestore, "artworks"), newArtwork);
        const newArtworkId = newDocRef.id;
        console.log("✅ New artwork created with ID:", newArtworkId);

        // 4. Copia le operations dalla subcollection
        try {
            const operationsRef = collection(firestore, "artworks", artworkId, "operations");
            const operationsSnapshot = await getDocs(operationsRef);

            console.log("📊 Found", operationsSnapshot.docs.length, "operations in subcollection to copy");

            const newOperationsRef = collection(firestore, "artworks", newArtworkId, "operations");

            if (operationsSnapshot.docs.length === 0) {
                console.log("⚠️ No operations found in subcollection");

                // Aggiungi solo l'operation di duplicazione
                await addDoc(newOperationsRef, {
                    type: "system",
                    description: `Artwork duplicated from "${originalData.title}" (no previous operations found)`,
                    outcome: "Artwork successfully duplicated",
                    date: Timestamp.now()
                });

                return newArtworkId;
            }

            // 5. Duplica ogni operation mantenendo la struttura originale
            for (const doc of operationsSnapshot.docs) {
                try {
                    const operationData = doc.data();

                    const newOperation = {
                        type: operationData.type || "unknown",
                        description: operationData.description
                            ? `${operationData.description}`
                            : `[Copied from original artwork]`,
                        outcome: operationData.outcome
                            ? `${operationData.outcome}`
                            : "",
                        date: operationData.date || Timestamp.now()
                    };

                    console.log("➕ Adding copied operation:", newOperation.type);
                    await addDoc(newOperationsRef, newOperation);
                    console.log("✅ Operation copied successfully");

                } catch (operationError) {
                    console.error("❌ Error copying operation:", operationError);
                }
            }

            // 6. Aggiungi l'operation di duplicazione
            await addDoc(newOperationsRef, {
                type: "system",
                description: `Artwork duplicated from "${originalData.title}" with ${operationsSnapshot.docs.length} operation(s) copied`,
                outcome: "Duplication completed successfully",
                date: Timestamp.now()
            });

            console.log("🎉 Artwork duplication completed successfully!");
            return newArtworkId;

        } catch (operationsError: any) {
            console.error("❌ Error handling operations:", operationsError);

            const newOperationsRef = collection(firestore, "artworks", newArtworkId, "operations");
            await addDoc(newOperationsRef, {
                type: "system",
                description: `Artwork duplicated from "${originalData.title}" (error copying operations: ${operationsError?.message || 'unknown error'})`,
                outcome: "Duplication completed with errors",
                date: Timestamp.now()
            });

            return newArtworkId;
        }

    } catch (error) {
        console.error("❌ Error duplicating artwork:", error);
        throw error;
    }
};

export const logOperation = async (artworkId: string, operation: any) => {
    try {
        console.log("📝 [logOperation] Starting operation for artwork:", artworkId);
        console.log("📝 [logOperation] Operation data:", operation);

        // ✅ RIMUOVI TUTTI I CAMPI UNDEFINED
        const cleanOperation = Object.keys(operation).reduce((acc: any, key) => {
            if (operation[key] !== undefined) {
                acc[key] = operation[key];
            }
            return acc;
        }, {});

        console.log("📝 [logOperation] Cleaned operation data:", cleanOperation);

        const operationsRef = collection(firestore, "artworks", artworkId, "operations");

        // ✅ AGGIUNGI TIMESTAMP SE NON PRESENTE
        const operationToSave = {
            ...cleanOperation,
            createdAt: cleanOperation.createdAt || serverTimestamp(),
            updatedAt: serverTimestamp(),
        };

        console.log("📝 [logOperation] Final operation to save:", operationToSave);

        const docRef = await addDoc(operationsRef, operationToSave);

        console.log("✅ [logOperation] Operation added with ID:", docRef.id);
        return docRef.id;
    } catch (error) {
        console.error("❌ [logOperation] Error:", error);
        throw error;
    }
};

// Nuova funzione per aggiornare lo stato di una next action
export const updateNextActionStatus = async (
    artworkId: string,
    operationId: string,
    status: 'pending' | 'completed' | 'cancelled',
    completionNotes?: string
) => {
    try {
        console.log("🔄 Updating next action status:", { artworkId, operationId, status });

        const operationRef = doc(firestore, "artworks", artworkId, "operations", operationId);

        const updateData: any = {
            nextActionStatus: status,
            updatedAt: Timestamp.now(),
        };

        if (status === 'completed') {
            updateData.nextActionCompletedAt = Timestamp.now();
        }

        if (completionNotes) {
            updateData.nextActionCompletionNotes = completionNotes;
        }

        await updateDoc(operationRef, updateData);

        console.log("✅ Next action status updated successfully");
    } catch (error) {
        console.error("❌ Error updating next action status:", error);
        throw error;
    }
};

// FUNZIONE debugOperations aggiornata
export const debugOperations = async (artworkId: string) => {
    try {
        console.log("🔍 Debug: Checking operations subcollection for artwork:", artworkId);

        const operationsRef = collection(firestore, "artworks", artworkId, "operations");
        const operationsSnapshot = await getDocs(operationsRef);

        console.log("📊 Operations in subcollection:", operationsSnapshot.docs.length);

        operationsSnapshot.docs.forEach(doc => {
            const data = doc.data();
            console.log("📝 Operation:", {
                id: doc.id,
                type: data.type,
                description: data.description,
                outcome: data.outcome,
                date: data.date
            });
        });

        return operationsSnapshot.docs.length;
    } catch (error) {
        console.error("❌ Debug error:", error);
        return 0;
    }
};

// Aggiungi questa funzione per ottenere tutti gli artwork
export const getAllArtworks = async (): Promise<Artwork[]> => {
    try {
        console.log("🔍 Getting all artworks from database...");

        const querySnapshot = await getDocs(
            query(collection(firestore, "artworks"), orderBy("createdAt", "desc"))
        );

        const artworks = querySnapshot.docs.map((doc) => {
            const data = doc.data();
            return {
                id: doc.id,
                ...data,
                createdAt: data.createdAt?.toDate() || new Date(),
                updatedAt: data.updatedAt?.toDate() || new Date(),
            } as Artwork;
        });

        console.log("✅ Retrieved", artworks.length, "artworks from database");
        return artworks;
    } catch (error) {
        console.error("Error getting all artworks: ", error);
        throw error;
    }
};

// Aggiorna getAllNextActions per includere lo stato
export const getAllNextActions = async (): Promise<{
    artworkId: string;
    artworkTitle: string;
    artworkArtist: string;
    artworkLocation: string;
    artworkStatus: string;
    operationId: string;
    operationType: string;
    operationDate: Date;
    nextAction: string;
    nextActionStatus: 'pending' | 'completed' | 'cancelled';
    nextActionCompletedAt?: Date;
    nextActionCompletionNotes?: string;
    operationNotes?: string;
}[]> => {
    try {
        console.log("🔍 Getting all next actions from database...");

        const artworks = await getAllArtworks();
        const nextActions: any[] = [];

        for (const artwork of artworks) {
            if (!artwork.id) continue;

            try {
                const operationsRef = collection(firestore, "artworks", artwork.id, "operations");
                const operationsQuery = query(operationsRef, orderBy("date", "desc"));
                const operationsSnapshot = await getDocs(operationsQuery);

                operationsSnapshot.docs.forEach((operationDoc) => {
                    const operationData = operationDoc.data();

                    if (operationData.nextAction && operationData.nextAction.trim()) {
                        nextActions.push({
                            artworkId: artwork.id!,
                            artworkTitle: artwork.title,
                            artworkArtist: artwork.artist,
                            artworkLocation: artwork.location,
                            artworkStatus: artwork.status,
                            operationId: operationDoc.id,
                            operationType: operationData.type,
                            operationDate: operationData.date?.toDate() || new Date(),
                            nextAction: operationData.nextAction.trim(),
                            nextActionStatus: operationData.nextActionStatus || 'pending',
                            nextActionCompletedAt: operationData.nextActionCompletedAt?.toDate(),
                            nextActionCompletionNotes: operationData.nextActionCompletionNotes,
                            operationNotes: operationData.notes || undefined,
                        });
                    }
                });

                console.log(`✅ Checked operations for artwork: ${artwork.title}`);
            } catch (operationError) {
                console.error(`Error getting operations for artwork ${artwork.id}:`, operationError);
            }
        }

        console.log(`✅ Found ${nextActions.length} next actions from ${artworks.length} artworks`);
        return nextActions;
    } catch (error) {
        console.error("Error getting all next actions: ", error);
        throw error;
    }
};